import { Selector, createSelector } from '@reduxjs/toolkit'
import { Client, clientApi } from 'api/client'
import { SimpleClient } from 'shared/models/Client'
import { RootState } from '../store'
import clientReducer from './reducer'

const _clientStore = (root: RootState) =>
  root[clientReducer.name] as {
    currentId: string | null
    clients: SimpleClient[]
    isSettingClient: boolean
  }

export const getActiveClientId: Selector<RootState, string | null> =
  createSelector(_clientStore, ({ currentId }) => currentId)

// `clientApi` (api/client.ts) adds a 'Client' tag type via `enhanceEndpoints`
// so `getClientsById`/`putClientsByIdAdvisorportalaccess` can invalidate the
// client cache correctly (see api/client.ts). That tag isn't known to the
// app's global RootState, which is built from the base, untagged
// `emptySplitApi` shared by every generated `api/*.ts` module — widening the
// global RootState itself would leak this module's tag type into every other
// enhanced api module and break their `.select()` calls. So the cast below is
// scoped locally to this file instead. It's safe: `provided.tags` is a plain
// runtime object RTK Query populates dynamically as tagged queries run, this
// only affects the compile-time view of it.
type RootStateWithClientTags = RootState & {
  portal: ReturnType<typeof clientApi.reducer>
}

export const clientApiSelector = (id: string | null) => (state: RootState) =>
  clientApi.endpoints.getClientsById.select({ id: id || '' })(
    state as RootStateWithClientTags
  )

export const clientSettingsApiSelector =
  (id: string | null) => (state: RootState) =>
    clientApi.endpoints.getClientsByIdSettings.select({ id: id || '' })(
      state as RootStateWithClientTags
    )

export const getActiveClient: Selector<RootState, Client | undefined> =
  createSelector(
    [(state: RootState) => clientApiSelector(getActiveClientId(state))(state)],
    (result) => result.data
  )

export const getClientSettings = createSelector(
  [
    (state: RootState) =>
      clientSettingsApiSelector(getActiveClientId(state))(state),
  ],
  (result) => result.data?.portalAvailableIds || []
)

export const getIsPortalLight = createSelector(
  [getActiveClient],
  (client) => client?.hasLightSystem || false
)

export const getClients = createSelector(_clientStore, ({ clients }) => clients)

export const getIsSettingClient = createSelector(
  _clientStore,
  ({ isSettingClient }) => isSettingClient
)
